"use client";

import { useQuery } from "@tanstack/react-query";
import { useProject } from "@/features/project/ProjectContext";
import { apiFetch } from "@/lib/api";
import type { Collection } from "@/types";

/**
 * The project's datasets.
 *
 * Replaces the legacy `useDatasets`, with two differences that come from the new
 * API rather than from choice:
 *
 *  - No `isDictionary` filter. That flag marked lookup tables to keep off the
 *    map; the REST model has no equivalent, and every collection is a synced
 *    Squidex schema. If dictionary collections reappear, the backend needs to say
 *    so — guessing from the slug here would be worse.
 *  - No auth gate. The legacy hook waited for authentication because datasets
 *    were a protected GraphQL field; the REST endpoints are not gated that way,
 *    so the query runs as soon as a project is resolved.
 */
export function collectionsQueryKey(projectId: string | undefined) {
  return ["collections", projectId] as const;
}

export function useCollections() {
  const project = useProject();
  const projectId = project?.id;

  return useQuery({
    queryKey: collectionsQueryKey(projectId),
    queryFn: ({ signal }) =>
      apiFetch<Collection[]>(`/projects/${projectId}/collections`, { signal }),
    enabled: !!projectId,
  });
}

/**
 * The project's primary dataset — the one the map and result list are built on.
 *
 * The backend orders collections by creation date, so "first" is the project's
 * oldest, which is stable across requests (the legacy `data[0]` relied on the
 * GraphQL response order, which was not).
 */
export function useCollection(): Collection | null {
  const { data } = useCollections();
  return data?.[0] ?? null;
}
